import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Plus, ArrowLeft, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import ProjectDetailsForm from './ProjectDetailsForm';
import { toast } from "sonner";

const AccountProject = ({ account, axiosInstance, onBack, currentUser }) => {
  const [projects, setProjects] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'form'
  const [selectedProject, setSelectedProject] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  useEffect(() => {
    fetchAccountProjects();
  }, [account]);

  const fetchAccountProjects = async () => {
    try {
      // Fetch all projects and filter by the current account's BDO ID
      // Assuming the backend endpoint returns all projects. 
      // Optimization: Ideally the backend would support ?bdo_id=... but filtering client side matches current pattern
      const response = await axiosInstance.get('/ccp/project-details');
      const accountProjects = response.data.filter(p => 
        (p.bdo_id === account.bdo_id || p.bdo_id === account.id) && 
        p.project_type !== 'Account_Placeholder'
      );
      setProjects(accountProjects);
    } catch (error) {
      console.error('Error fetching account projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const handleAddClick = () => {
    setNewProjectName('');
    setIsCreateDialogOpen(true);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    
    try {
      const payload = {
        bdo_id: account.bdo_id || account.id,
        bdo_name: account.bdo_name || account.full_name,
        project_name: newProjectName, // Changed from project_type to project_name
        project_type: '' // Initialize as empty, will be selected in form
      };

      const response = await axiosInstance.post('/ccp/project-details', payload);
      const newProject = response.data;

      // Update list
      setProjects([newProject, ...projects]);
      
      // Select and open form
      setSelectedProject(newProject);
      setIsCreateDialogOpen(false);
      setViewMode('form');
      toast.success('Project created successfully');

    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleTileClick = (project) => {
    setSelectedProject(project);
    setViewMode('form');
  };

  const handleFormBack = () => {
    setViewMode('list');
    setSelectedProject(null);
    fetchAccountProjects(); // Refresh list
  };

  const handleSaveProject = async (projectId, data) => {
    try {
      if (projectId) {
        // Update existing
        await axiosInstance.put(`/ccp/project-details/${projectId}`, data);
        toast.success('Project updated successfully');
      } else {
        // Create new
        // We need to ensure the payload includes the BDO ID/Name from the parent account
        const payload = {
          ...data,
          bdo_id: account.bdo_id || account.id,
          bdo_name: account.bdo_name || account.full_name
        };
        await axiosInstance.post('/ccp/project-details', payload);
        toast.success('Project added successfully');
      }
      return true;
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
      return false;
    }
  };

  const confirmDeleteProject = (project) => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const executeDeleteProject = async () => {
      if (!projectToDelete) return;
      
      try {
          await axiosInstance.delete(`/ccp/project-details/${projectToDelete.id}`);
          setProjects(projects.filter(p => p.id !== projectToDelete.id));
          toast.success('Project removed successfully');
          setIsDeleteDialogOpen(false);
          setProjectToDelete(null);
      } catch (error) {
          console.error('Error removing project:', error);
          toast.error('Failed to remove project');
      }
    };

  if (viewMode === 'form') {
    return (
      <ProjectDetailsForm 
        account={account}
        project={selectedProject}
        onBack={handleFormBack}
        onSave={handleSaveProject}
        axiosInstance={axiosInstance}
        currentUser={currentUser}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="pl-0 hover:pl-2 transition-all">
                <ArrowLeft className="h-5 w-5 mr-2" />
            </Button>
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{account.bdo_name || account.full_name}</h2>
                <p className="text-gray-600 dark:text-gray-200">Manage projects for this account</p>
            </div>
        </div>
        <Button onClick={handleAddClick} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="h-4 w-4 mr-2" />
          Add Project
        </Button>
      </div>

      {/* Tiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">No projects found. Click "+ Add Project" to create one.</p>
          </div>
        ) : (
          projects.map((project) => {
            const isDgmUser = (currentUser?.email?.trim().toLowerCase() === 'dgm@isourceasia.com') || (currentUser?.full_name?.toUpperCase() === 'DONALD G. MADINO');
            const isCadfUser = (currentUser?.email?.trim().toLowerCase() === 'cadf@isourceasiaworkplace.com');
            const isOamUser = (currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com');
            const isJcpUser = (currentUser?.email?.trim().toLowerCase() === 'jcp@isourceasia.com');
            const isMspUser = (currentUser?.email?.trim().toLowerCase() === 'msp@isourceasia.com.ph');
            
            let showBadge = false;
            
            const isApproved = project.is_approved === true || project.is_approved === 'true' || project.is_approved === 1;
            const isApprovedPricing = project.is_approved_pricing === true || project.is_approved_pricing === 'true' || project.is_approved_pricing === 1;
            const isApprovedCosts = project.is_approved_costs === true || project.is_approved_costs === 'true' || project.is_approved_costs === 1;
            const isDisapprovedCosts = project.is_disapproved_costs === true || project.is_disapproved_costs === 'true' || project.is_disapproved_costs === 1;
            const isApprovedJoygie = project.is_approved_joygie === true || project.is_approved_joygie === 'true' || project.is_approved_joygie === 1;
            const isApprovedOverall = project.is_approved_overall === true || project.is_approved_overall === 'true' || project.is_approved_overall === 1;

            const approvedCount = [isApproved, isApprovedPricing, isApprovedCosts, isApprovedJoygie, isApprovedOverall].filter(Boolean).length;

            const statusText = isDisapprovedCosts
              ? 'Project Disapproved'
              : approvedCount === 5
                ? 'Project Approved'
                : `Checking ${approvedCount * 20}%`;

            const statusClassName = isDisapprovedCosts
              ? 'text-red-600 font-bold'
              : approvedCount === 5
                ? 'text-green-600 font-bold'
                : 'text-yellow-500 font-bold';

            if (isDisapprovedCosts) {
                showBadge = false;
            } else if (isDgmUser) {
                showBadge = !isApproved;
            } else if (isCadfUser) {
                showBadge = isApproved && !isApprovedPricing;
            } else if (isOamUser) {
                showBadge = isApprovedPricing && !isApprovedCosts;
            } else if (isJcpUser) {
                showBadge = isApprovedCosts && !isApprovedJoygie;
            } else if (isMspUser) {
                showBadge = isApprovedJoygie && !isApprovedOverall;
            }
            
            return (
            <Card 
              key={project.id} 
              className="glass shadow-md hover:shadow-lg transition-all cursor-pointer group border-l-4 border-l-green-500"
              onClick={() => handleTileClick(project)}
            >
              <CardContent className="p-6 flex items-center justify-between min-h-[120px] relative">
                {showBadge && (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-sm z-10">
                      1
                    </div>
                )}
                <div className="flex flex-col justify-center text-left">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                    {project.project_name || project.project_type || 'Untitled Project'}
                  </h3>
                  {project.pcc_number && (
                      <p className="text-sm text-gray-500">{project.pcc_number}</p>
                  )}
                </div>

                <div className="flex flex-col items-end justify-center text-right">
                  <p className={statusClassName}>{statusText}</p>
                  {project.end_user && (
                      <p className="text-xs text-gray-400">End User: {project.end_user}</p>
                  )}
                </div>
                
                {/* Remove button at bottom right */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDeleteProject(project);
                  }}
                  className="absolute bottom-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-auto px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </CardContent>
            </Card>
            )
          })
        )}
      </div>

      {/* Create Project Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Enter the name of the project to proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Input
                id="name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Project Name"
                className="col-span-4"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateProject();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateProject}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the project "{projectToDelete?.project_name || projectToDelete?.project_type || 'Untitled'}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={executeDeleteProject}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountProject;
